import type { JSX } from 'react';

import type { PrefetchedDataProps, PrefetchedUserDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import type {
	PetitionDetailsPagePrefetchedContext,
	PetitionDetailsPagePrefetchedUserContext,
} from 'src/app/pages/petitionDetails/pageContext';
import { prefetchPageContext, prefetchPageUserContext } from 'src/app/pages/petitionDetails/pageContext';
import type { PetitionHeadlineBanditData, Sharer } from 'src/app/pages/petitionDetails/shared/types';
import { headlineReplacements } from 'src/app/pages/petitionDetails/shared/utilities';
import { isLoaded } from 'src/app/shared/utils/async';

import { getHeadlineBanditVariantText, getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';
import {
	buildMetaContext,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from '../shared';
import type { PetitionDetailsMetaContext } from '../shared';

type PrefetchedData = PetitionDetailsPagePrefetchedContext &
	Readonly<{
		metaContext: PetitionDetailsMetaContext;
		sharer?: Sharer;
		headlineVariant?: PetitionHeadlineBanditData;
	}>;

const ALLOWED_SHARE_TYPES = ['wa', 'cl_', 'fb', 'tw'];

export const PetitionDetailsBanditV2 = withPrefetchedData(
	({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> &
		PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>): JSX.Element => {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: {
					slug: slugParam,
					shareType: shareTypeParam,
					experimentVariantName: experimentVariantNameParam,
					sharerUserId: sharerUserIdParam,
				},
			} = context;

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const slug = slugParam!;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const experimentVariantName = experimentVariantNameParam!;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const sharerUserId = sharerUserIdParam!;
			const shareType = shareTypeParam && ALLOWED_SHARE_TYPES.includes(shareTypeParam) ? shareTypeParam : undefined;

			const [pageContext, headlineVariant, sharer] = await Promise.all([
				prefetchPageContext(slug, context),
				shareType
					? getHeadlineBanditVariantText(shareType, slug, experimentVariantName, context.utilityContext).catch(
							() => undefined,
						)
					: undefined,
				getSharer(sharerUserId, context.utilityContext).catch(() => undefined),
			]);

			return {
				...pageContext,
				metaContext: buildMetaContext(context),
				headlineVariant,
				sharer,
			};
		},
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const shareHeadlineOverrides = (() => {
				if (prefetchedData.headlineVariant) {
					return {
						title: prefetchedData.headlineVariant.headline,
						description: prefetchedData.headlineVariant.description,
					};
				}
				if (prefetchedData.sharer) {
					const replacements = headlineReplacements({
						pageData: prefetchedData.pageData.pageData,
						sharer: prefetchedData.sharer,
						i18n,
					});
					return {
						title: i18n.translate('fe.components.petition_share.share_text_fb', replacements),
						description: replacements.petition_name as string,
					};
				}
				return undefined;
			})();
			return petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				metaContext: prefetchedData.metaContext,
				shareHeadlineOverrides,
			});
		},
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
	},
);
